import { useState } from 'react'
import { WidgetFrame, type WidgetLoadState } from '../../insights/widgets/WidgetFrame'
import { WidgetErrorBoundary } from '../../insights/widgets/WidgetErrorBoundary'
import { DonutChartWrapper, type DonutSlice } from '../../insights/charts/DonutChart'
import { GaugeTile } from '../../insights/charts/GaugeTile'
import { WaterfallChart, type WaterfallStep } from '../../insights/charts/WaterfallChart'
import { AdjustmentsHistoryTable, type AdjustmentHistoryRowView } from './AdjustmentsHistoryTable'
import { FilingStatusTimeline, type FilingStatusStep } from './FilingStatusTimeline'

/** One filing-scoped section's load state + payload — the same 5-state contract
 * (`WidgetLoadState`) every existing dashboard tile already uses, so `WidgetFrame` renders each
 * section's loading/empty/error/not_authorized states identically to the portfolio dashboard. */
export interface SectionState<T> {
  status: WidgetLoadState
  message?: string | null
  data?: T
}

export interface CaseAnalyticsHeader {
  creditComputedDisplay: string | null
  qreTotalDisplay: string | null
  readinessPercent: number | null
  filingStatusLabel: string | null
}

export interface FilingOption {
  id: string
  label: string
}

export interface CaseAnalyticsPanelProps {
  header: CaseAnalyticsHeader
  /** Multiple `ProgramFiling`s per Case (federal + states) — a segmented-control switch, ≤1 click
   * (design-system.md §6.3's own named budget: "federal ↔ state filing switch ≤1"). Rendered only
   * when there is more than one filing; a single-filing case skips the control entirely. */
  filings: FilingOption[]
  selectedFilingId: string | null
  onSelectFiling: (filingId: string) => void

  spend: SectionState<{ slices: DonutSlice[]; totalDisplay: string }>
  onSpendSliceClick: (slice: DonutSlice) => void
  onViewSpendTab: () => void
  onRetrySpend: () => void

  /** `steps: []` once the fetch itself succeeds but no FINAL `ComputationRun` exists yet — a
   * legitimate "ok, nothing there yet" content case (mirrors `timeline`/`adjustments` below), never
   * `WidgetFrame`'s network-level `empty`/`error` framing (§8 note below). */
  computation: SectionState<{ steps: WaterfallStep[] }>
  onRetryComputation: () => void

  /** `percent: null` once the fetch itself succeeds but no `ReadinessScore` has been computed yet —
   * same "ok, nothing there yet" convention as `computation` above. */
  readiness: SectionState<{ percent: number | null; gaps: { id: string; label: string }[] }>
  onComputeReadinessNow: () => void
  onGapClick: (gapId: string) => void
  onRetryReadiness: () => void

  timeline: SectionState<{ steps: FilingStatusStep[]; additionalEventCount: number }>
  onViewFullTimeline: () => void
  onRetryTimeline: () => void

  adjustments: SectionState<{ rows: AdjustmentHistoryRowView[] }>
  onRetryAdjustments: () => void
}

/** Mirrors `OverviewPanel.tsx#StatCard` exactly (same tokens/layout) — a candidate for extraction
 * to a shared component once a third page wants the same chip, not duplicated speculatively here. */
function KpiChip({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        flex: '1 1 180px',
        minWidth: 180,
        padding: 'var(--iw-space-3)',
        background: 'var(--iw-neutral-100)',
        border: '1px solid var(--iw-border)',
        borderRadius: 'var(--iw-radius)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>{label}</span>
      <span className="iw-kpi-value">{value}</span>
    </div>
  )
}

/**
 * The `/cases/{id}/analytics` tab body (IB-282 View 2, docs/design/IB-282-view2-per-case-design.md
 * §2-§5). Purely presentational — every value arrives via props already resolved from the real
 * endpoints named in the DoR (`ComputationRunController`, `ReadinessScoreController`, the universal
 * events Timeline API, `QualifiedSpendLineApi`, `AdjustmentHistoryPort`); a follow-on data-wiring
 * pass (`useCaseAnalytics(caseId, filingId)`) owns the fetch/compose/retry lifecycle per section and
 * feeds this component, mirroring `useWidgetData`'s per-tile independence (AC26/IB-162 posture) —
 * one section's fetch failure never blanks the others.
 *
 * Layout: KPI header row (credit computed / QRE total / readiness % / filing status), the existing
 * `.iw-dashboard-grid` responsive auto-fit grid (charts.css) for the 5 sections, each wrapped in
 * `WidgetErrorBoundary` (thrown-render containment) + `WidgetFrame` (reported-failure containment) —
 * the same two-layer isolation every tile on `/insights/dashboards` already uses (IB-162).
 *
 * <b>`WidgetFrame`'s `state` carries only network/authz framing (`loading`/`error`/
 * `not_authorized`), never domain emptiness.</b> A successful fetch that simply found nothing yet
 * (no FINAL `ComputationRun`, no `ReadinessScore` computed, zero timeline milestones reached, zero
 * adjustments) is `status: 'ok'` with a domain-empty payload — this component's own children render
 * that "nothing yet" message (mirroring `FilingStatusTimeline`/`AdjustmentsHistoryTable`'s own
 * internal empty-state paragraphs), never `WidgetFrame`'s generic `empty` state. This matters
 * concretely for the "Compute now" CTAs: `WidgetFrame` only ever renders its header `drill`/
 * `exportAction` button when `state === 'ok'` (by design, shared with every `/insights/dashboards`
 * tile) — modeling "not yet scored" as `state: 'empty'` would make a same-tile action button
 * structurally unrenderable. The readiness tile's real, parameterless "Compute now" action
 * (`POST .../readiness-score`, no ambiguous params to guess) is therefore a plain in-tile button,
 * not a `WidgetFrame` `drill`. The computation tile intentionally ships NO "Compute now" action:
 * creating a brand-new `ComputationRun` needs a `kind`/`asOf`/`inputBindings` choice
 * (`POST /program-filings/{id}/computation-runs`) that no screen in this codebase makes for a user
 * today — inventing a blind default here would be exactly the fabricated-action CLAUDE.md forbids.
 * Disclosed as a named gap (STUBS_DECLARED), not silently dropped.
 */
export function CaseAnalyticsPanel({
  header,
  filings,
  selectedFilingId,
  onSelectFiling,
  spend,
  onSpendSliceClick,
  onViewSpendTab,
  onRetrySpend,
  computation,
  onRetryComputation,
  readiness,
  onComputeReadinessNow,
  onGapClick,
  onRetryReadiness,
  timeline,
  onViewFullTimeline,
  onRetryTimeline,
  adjustments,
  onRetryAdjustments,
}: CaseAnalyticsPanelProps) {
  const [highlightedCategory, setHighlightedCategory] = useState<string | null>(null)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      {filings.length > 1 ? (
        <div role="tablist" aria-label="Filing" style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
          {filings.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={selectedFilingId === f.id}
              className="iw-btn iw-btn--sm iw-btn--ghost"
              style={selectedFilingId === f.id ? { border: '1px solid var(--iw-accent)', color: 'var(--iw-accent)' } : undefined}
              onClick={() => onSelectFiling(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--iw-space-3)' }}>
        <KpiChip label="Credit computed" value={header.creditComputedDisplay ?? '—'} />
        <KpiChip label="Qualified spend" value={header.qreTotalDisplay ?? '—'} />
        <KpiChip label="Evidence readiness" value={header.readinessPercent !== null ? `${header.readinessPercent}%` : '—'} />
        <KpiChip label="Filing status" value={header.filingStatusLabel ?? '—'} />
      </div>

      <div className="iw-dashboard-grid">
        <WidgetErrorBoundary title="QRE / qualified-spend breakdown" icon="bar-chart">
          <WidgetFrame
            title="QRE / qualified-spend breakdown"
            icon="bar-chart"
            state={spend.status}
            message={spend.message ?? 'No qualified spend recorded yet.'}
            onRetry={onRetrySpend}
            drill={{ label: 'View spend detail', onClick: onViewSpendTab }}
          >
            {spend.data ? (() => {
              const spendData = spend.data
              const total = spendData.slices.reduce((sum, s) => sum + s.value, 0)
              return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
                <span className="iw-kpi-value">{spendData.totalDisplay}</span>
                <DonutChartWrapper
                  title="QRE / qualified-spend by category"
                  slices={spendData.slices}
                  onSliceClick={(slice) => {
                    setHighlightedCategory((current) => (current === slice.key ? null : slice.key))
                    onSpendSliceClick(slice)
                  }}
                />
                {/* In-tile category detail list — the donut slice's cross-filter target (DoR AC2.1,
                    RAID R-UX282-2: same tile, same page, never a navigation). */}
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {spendData.slices.map((slice) => {
                    const share = total > 0 ? `${((slice.value / total) * 100).toFixed(1)}%` : '0%'
                    return (
                      <li key={slice.key}>
                        <button
                          type="button"
                          className="iw-btn iw-btn--sm iw-btn--ghost"
                          aria-pressed={highlightedCategory === slice.key}
                          style={
                            highlightedCategory === slice.key
                              ? { border: '1px solid var(--iw-accent)', color: 'var(--iw-accent)', width: '100%', justifyContent: 'space-between' }
                              : { width: '100%', justifyContent: 'space-between' }
                          }
                          onClick={() => {
                            setHighlightedCategory((current) => (current === slice.key ? null : slice.key))
                            onSpendSliceClick(slice)
                          }}
                        >
                          <span>{slice.label}</span>
                          <span>{share}</span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
              )
            })() : null}
          </WidgetFrame>
        </WidgetErrorBoundary>

        <WidgetErrorBoundary title="Credit computation" icon="list-tree">
          <WidgetFrame
            title="Credit computation"
            icon="list-tree"
            state={computation.status}
            message={computation.message}
            onRetry={onRetryComputation}
          >
            {computation.data ? (
              computation.data.steps.length > 0 ? (
                <WaterfallChart title="Base → uplifts → cap → net" steps={computation.data.steps} />
              ) : (
                <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
                  Not yet computed. No finalized computation run exists yet for this filing.
                </p>
              )
            ) : null}
          </WidgetFrame>
        </WidgetErrorBoundary>

        <WidgetErrorBoundary title="Evidence readiness" icon="check-circle">
          <WidgetFrame
            title="Evidence readiness"
            icon="check-circle"
            state={readiness.status}
            message={readiness.message}
            onRetry={onRetryReadiness}
          >
            {readiness.data ? (
              readiness.data.percent === null ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)', alignItems: 'flex-start' }}>
                  <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>Not yet scored.</p>
                  <button type="button" className="iw-btn iw-btn--sm" onClick={onComputeReadinessNow}>
                    Compute now
                  </button>
                </div>
              ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
                <GaugeTile title="Readiness score" percent={readiness.data.percent} />
                {readiness.data.gaps.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: 'var(--iw-space-4)' }}>
                    {readiness.data.gaps.map((g) => (
                      <li key={g.id}>
                        <button type="button" className="iw-btn iw-btn--sm iw-btn--ghost" onClick={() => onGapClick(g.id)}>
                          {g.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
                    No blocking requirements.
                  </p>
                )}
              </div>
              )
            ) : null}
          </WidgetFrame>
        </WidgetErrorBoundary>

        <WidgetErrorBoundary title="Filing-status timeline" icon="clock">
          <WidgetFrame title="Filing-status timeline" icon="clock" state={timeline.status} message={timeline.message} onRetry={onRetryTimeline}>
            {timeline.data ? (
              <FilingStatusTimeline
                steps={timeline.data.steps}
                additionalEventCount={timeline.data.additionalEventCount}
                onViewFullTimeline={onViewFullTimeline}
              />
            ) : null}
          </WidgetFrame>
        </WidgetErrorBoundary>

        <WidgetErrorBoundary title="Adjustments history" icon="edit-3">
          <WidgetFrame title="Adjustments history" icon="edit-3" state={adjustments.status} message={adjustments.message} onRetry={onRetryAdjustments}>
            {adjustments.data ? <AdjustmentsHistoryTable rows={adjustments.data.rows} /> : null}
          </WidgetFrame>
        </WidgetErrorBoundary>
      </div>
    </div>
  )
}
